import uploadsService from "../services/uploads.service";
import { backendUrl } from "../Router/constant";

const readMessage = (resp, fallback) =>
  resp?.data?.data?.error ||
  resp?.data?.error ||
  resp?.data?.data?.message ||
  resp?.data?.message ||
  fallback;

const ok = (resp) => resp?.status === 200 || resp?.status === 201;

// Base64 inflates by about a third on the wire, and the whole thing goes as
// one JSON body, so keep the source file modest.
export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

/**
 * FileReader hands back a data URL ("data:image/png;base64,AAAA"). The API
 * field is `fileBase64`, so the prefix is stripped — a server doing
 * Buffer.from(value, "base64") would otherwise decode the header as content.
 */
const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read that file."));
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });

/** A relative path from the API is only useful once it's absolute. */
const absoluteUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("data:")) return path;

  return `${String(backendUrl).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
};

/**
 * Uploads a File and resolves to the stored URL.
 * @param {File} file
 */
export const uploadFile = (file) => async () => {
  if (!file) return { success: false, message: "No file selected." };

  if (file.size > MAX_UPLOAD_BYTES) {
    return {
      success: false,
      message: "That image is over 2MB. Please choose a smaller one.",
    };
  }

  let fileBase64;

  try {
    fileBase64 = await readAsBase64(file);
  } catch (error) {
    return { success: false, message: error.message };
  }

  const resp = await uploadsService.uploadFile({
    fileBase64,
    fileName: file.name,
  });

  const body = resp?.data?.data || resp?.data || {};
  const path = body.path || body.url || body.fileName;

  if (ok(resp) && path) {
    return { success: true, response: resp, url: absoluteUrl(path), body };
  }

  return {
    success: false,
    response: resp,
    message: readMessage(
      resp,
      ok(resp)
        ? "The upload succeeded but no file path came back."
        : "We couldn't upload that image. Please try again.",
    ),
  };
};
